import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useLanguageStore } from '../../stores/languageStore';
import { categoryApi } from '../../utils/api';
import { Category } from '../../types';
import toast from 'react-hot-toast';

const AdminCategories: React.FC = () => {
  const { user, isAuthenticated } = useAuthStore();
  const { language } = useLanguageStore();
  const [categories, setCategories] = React.useState<Category[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [editingId, setEditingId] = React.useState<number | null>(null);
  const [formData, setFormData] = React.useState({
    name: '',
    description: '',
    parent_id: '',
  });
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [categoryToDelete, setCategoryToDelete] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (user?.role !== '管理员' && user?.role !== '站长') {
      return;
    }
    fetchCategories();
  }, [user]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await categoryApi.getAllCategoriesForAdmin();
      if (response.success) {
        setCategories(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('请输入版块名称');
      return;
    }

    try {
      const response = await categoryApi.createCategory({
        name: formData.name,
        description: formData.description,
        parent_id: formData.parent_id ? parseInt(formData.parent_id) : undefined,
      });
      if (response.success) {
        toast.success('创建成功');
        setFormData({ name: '', description: '', parent_id: '' });
        fetchCategories();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || '创建失败');
    }
  };

  const handleUpdate = async (id: number, data: Partial<Category>) => {
    try {
      const response = await categoryApi.updateCategory(id, data);
      if (response.success) {
        toast.success('更新成功');
        setEditingId(null);
        fetchCategories();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || '更新失败');
    }
  };

  // 显示删除确认对话框
  const handleDeleteClick = (id: number) => {
    setCategoryToDelete(id);
    setShowDeleteModal(true);
  };

  // 执行删除操作
  const confirmDelete = async () => {
    if (categoryToDelete === null) return;
    
    try {
      const response = await categoryApi.deleteCategory(categoryToDelete);
      if (response.success) {
        toast.success('删除成功');
        fetchCategories();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || '删除失败');
    } finally {
      setShowDeleteModal(false);
      setCategoryToDelete(null);
    }
  };

  // 取消删除操作
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setCategoryToDelete(null);
  };

  if (!isAuthenticated || (user?.role !== '管理员' && user?.role !== '站长')) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">{'版块管理'}</h1>

      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{'创建版块'}</h2>
        <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder={'版块名称'}
            className="input"
          />
          <input
            type="text"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder={'版块描述（可选）'}
            className="input"
          />
          <select
            value={formData.parent_id}
            onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
            className="input"
          >
            <option value="">{'作为顶级版块'}</option>
            {categories
              .filter((c) => !c.parent_id)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {'作为'} {c.name} {'的子版块'}
                </option>
              ))}
          </select>
          <button type="submit" className="btn btn-primary md:col-span-3">
            {'创建版块'}
          </button>
        </form>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {'版块名称'}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {'描述'}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {'帖子数'}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {'状态'}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                {'操作'}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {(() => {
              const renderCategory = (category: Category, level: number = 0) => {
                const indent = level * 20;
                return (
                  <>
                    <tr key={category.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span 
                          className="inline-block"
                          style={{ paddingLeft: `${indent}px` }}
                        >
                          {level > 0 && '└ '}
                          <span className={level > 0 ? 'text-gray-600' : 'font-medium text-gray-900'}>
                            {category.name}
                          </span>
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                        {category.description || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {category.posts_count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`badge ${category.status === 'active' ? 'badge-success' : 'badge-danger'}`}>
                          {category.status === 'active' ? ('显示') : ('隐藏')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleUpdate(category.id, {
                            status: category.status === 'active' ? 'hidden' : 'active',
                          })}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          {category.status === 'active' ? ('隐藏') : ('显示')}
                        </button>
                        <button
                          onClick={() => handleDeleteClick(category.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          {'删除'}
                        </button>
                      </td>
                    </tr>
                    {category.children && category.children.map((child) => renderCategory(child, level + 1))}
                  </>
                );
              };

              return categories.map((category) => renderCategory(category, 0));
            })()}
          </tbody>
        </table>
      </div>

      {/* 删除确认模态框 */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h2 className="text-xl font-bold mb-4">{'确认删除'}</h2>
            <p className="mb-4">{'确定要删除该版块吗？此操作不可撤销。'}</p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
              >
                {'取消'}
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
              >
                {'确认删除'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategories;
